import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Per-side switch: keep the generated panel in the layout/quote but hide its shape on the
// sheet when the shop's "Show physical pieces" setting is on.
const HidePanelRow = ({ edge, onEdge }) => {
  const shown = !edge?.hide_panel;
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2"
      data-testid="designer-edges-hide-panel-row"
    >
      <div>
        <div className="text-xs font-semibold text-zinc-200">
          Draw as separate piece
        </div>
        <div className="text-[11px] text-zinc-500">
          Shows beside this side on the sheet. Still cut and priced.
        </div>
      </div>
      <Switch
        checked={shown}
        data-testid="designer-edges-hide-panel"
        onCheckedChange={(v) => onEdge?.({ hide_panel: !v })}
      />
    </div>
  );
};

// Cross-section of the stone edge, drawn the way a fabricator sketches it
const PATHS = {
  eased: "M4 10 H52 Q56 10 56 14 V26 Q56 30 52 30 H4 Z",
  bevel: "M4 10 H50 L56 16 V30 H4 Z",
  "half-bullnose": "M4 10 H50 Q56 10 56 18 Q56 24 50 30 H4 Z",
  bullnose: "M4 10 H46 Q56 10 56 20 Q56 30 46 30 H4 Z",
  ogee: "M4 10 H48 Q56 10 50 18 Q44 24 52 30 H4 Z",
  "round-over": "M4 10 H48 Q56 10 56 18 V30 H4 Z",
  "double-round-over": "M4 10 H48 Q56 10 56 18 Q56 26 48 30 H4 Z",
  mitered: "M4 10 H56 V30 H4 Z",
  chisel: "M4 10 H52 L56 13 L52 16 L56 19 L52 22 L56 25 L52 28 L56 30 H4 Z",
  pencil: "M4 10 H52 Q56 12 56 20 Q56 28 52 30 H4 Z",
  cove: "M4 10 H56 Q48 20 56 30 H4 Z",
  waterfall: "M4 10 H56 V30 H4 Z",
  unfinished: "M4 10 H56 V30 H4 Z",
};

export const EdgeShape = ({ shape = "eased", color = "#a5b4fc" }) => {
  const kind = (shape || "eased").toLowerCase();
  return (
    <svg viewBox="0 0 60 44" className="h-14 w-full">
      <path
        d={PATHS[kind] || PATHS.eased}
        fill="rgba(255,255,255,0.04)"
        stroke={color}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {kind === "mitered" && (
        <path d="M44 10 L56 22" stroke={color} strokeWidth="1.1" fill="none" />
      )}
      {kind === "waterfall" && (
        <path
          d="M50 30 V40 M56 30 V40"
          stroke={color}
          strokeWidth="1.2"
          fill="none"
        />
      )}
      {kind === "unfinished" && (
        <>
          <circle
            cx="30"
            cy="20"
            r="7"
            fill="none"
            stroke="#f87171"
            strokeWidth="1.3"
          />
          <path d="M25 25 L35 15" stroke="#f87171" strokeWidth="1.3" />
        </>
      )}
    </svg>
  );
};

const TABS = [
  ["profile", "Profile"],
  ["backsplash", "Backsplash"],
  ["waterfall", "Waterfall"],
];

const Row = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    {children}
    {hint && (
      <span className="font-mono-data block text-[10px] text-emerald-300">
        {hint}
      </span>
    )}
  </div>
);

/** Right-hand Edges panel: pick a profile by picture, set backsplash or waterfall. */
export const EdgePanel = ({
  styles = [],
  profiles = [],
  edge = {},
  edgeIndex = 0,
  edgeKind = "finished",
  onProfile,
  onEdge,
  onEdgeType,
  onCopyToAll,
  onReset,
  onClose,
}) => {
  const { can } = usePermissions();
  const [tab, setTab] = useState("profile");
  const [list, setList] = useState(styles);
  const [services, setServices] = useState([]);

  useEffect(() => setList(styles), [styles]);

  useEffect(() => {
    api
      .get("/services")
      .then(({ data }) =>
        setServices(
          (Array.isArray(data) ? data : []).filter((s) => s.active !== false),
        ),
      )
      .catch(() => setServices([]));
  }, []);

  const cards = (
    list.length
      ? list.filter((s) => s.visible !== false)
      : profiles.map((name) => ({ id: name, name, shape: "eased" }))
  ).slice(0, 40);

  const current = edge?.profile || cards[0]?.name || "Eased";
  const style = cards.find((s) => s.name === current);
  const splashSqft = round2(
    ((Number(edge?.backsplash_height_in) || 0) * (edge?.length_in || 0)) / 144,
  );
  const dropSqft = round2(
    ((Number(edge?.drop_in) || 0) * (edge?.length_in || 0)) / 144,
  );

  const linkService = async (serviceId) => {
    const edges = list.map((s) =>
      s.id === style?.id
        ? { ...s, service_id: serviceId === "none" ? "" : serviceId }
        : s,
    );
    setList(edges);
    try {
      await api.put("/settings/drawing", { edges });
      toast.success("Linked service saved");
    } catch {
      setList(list);
      toast.error("Could not save the linked service");
    }
  };

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-950"
      data-testid="designer-edges-panel"
    >
      <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2.5">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-200">
          Edges · side {edgeIndex + 1}
        </span>
        {onClose && (
          <button
            type="button"
            data-testid="designer-edges-panel-close"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X size={13} weight="bold" />
          </button>
        )}
      </div>

      <div className="flex gap-4 border-b border-zinc-800 px-3">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`designer-edges-tab-${key}`}
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              tab === key
                ? "border-indigo-400 text-indigo-200"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="p-3">
          <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Profiles
          </span>
          <div
            className="mt-2 grid max-h-[26rem] grid-cols-2 gap-2 overflow-y-auto pr-1"
            data-testid="designer-profile-grid"
          >
            {cards.map((s) => {
              const on = s.name === current;
              return (
                <button
                  key={s.id || s.name}
                  type="button"
                  data-testid={`designer-profile-card-${s.name}`}
                  onClick={() => onProfile?.(s.name)}
                  className={`relative rounded-lg border p-2 text-left transition-colors duration-150 ${
                    on
                      ? "border-indigo-400 bg-indigo-500/15"
                      : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"
                  }`}
                >
                  {s.default && (
                    <span className="absolute left-1.5 top-1.5 rounded bg-indigo-500 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-white">
                      Default
                    </span>
                  )}
                  <EdgeShape shape={s.shape} color={s.color || "#a5b4fc"} />
                  <span className="mt-1 block text-[11px] font-semibold leading-tight text-zinc-200">
                    {s.name}
                  </span>
                  {(s.alias || s.abbr) && (
                    <span className="font-mono-data block text-[9px] uppercase tracking-widest text-zinc-500">
                      {s.alias || s.abbr}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === "backsplash" && (
        <div className="space-y-3 p-3">
          <Row
            label="Backsplash height (in)"
            hint={`${splashSqft} ft² on this side`}
          >
            <Input
              data-testid="designer-edges-splash"
              type="number"
              step="any"
              value={edge?.backsplash_height_in ?? 0}
              onChange={(e) =>
                onEdge?.({ backsplash_height_in: Number(e.target.value) || 0 })
              }
              className="h-10 border-zinc-800 bg-zinc-950"
            />
          </Row>
          <div className="flex flex-wrap gap-1.5">
            {[0, 4, 6, 18].map((h) => (
              <button
                key={h}
                type="button"
                data-testid={`designer-edges-splash-${h}`}
                onClick={() => onEdge?.({ backsplash_height_in: h })}
                className="h-8 rounded-full border border-zinc-800 bg-zinc-900 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-400/40"
              >
                {h === 0 ? "None" : `${h}"`}
              </button>
            ))}
          </div>
          {Number(edge?.backsplash_height_in) > 0 && (
            <HidePanelRow edge={edge} onEdge={onEdge} />
          )}
        </div>
      )}

      {tab === "waterfall" && (
        <div className="space-y-3 p-3">
          <Row label="Side type">
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ["finished", "Finished"],
                ["seam", "Seam"],
                ["waterfall", "Waterfall"],
                ["mitre", "Mitre"],
                ["fridge", "Fridge"],
                ["range", "Stove"],
                ["dishwasher", "Dishwasher"],
                ["hood", "Hood"],
              ].map(([kind, label]) => (
                <button
                  key={kind}
                  type="button"
                  data-testid={`designer-edges-kind-${kind}`}
                  onClick={() => onEdgeType?.(kind)}
                  className={`h-10 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    edgeKind === kind
                      ? "border-indigo-400 bg-indigo-500/15 text-indigo-200"
                      : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Row>
          {["fridge", "range", "dishwasher", "hood"].includes(edgeKind) && (
            <Row
              label="Faces"
              hint="Turn the appliance so its front faces the room"
            >
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  ["out", "Out"],
                  ["left", "Left"],
                  ["right", "Right"],
                  ["in", "In"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`designer-edges-facing-${val}`}
                    onClick={() => onEdge?.({ appliance_facing: val })}
                    className={`h-9 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      (edge?.appliance_facing || "out") === val
                        ? "border-indigo-400 bg-indigo-500/15 text-indigo-200"
                        : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Row>
          )}
          {["fridge", "range", "dishwasher", "hood"].includes(edgeKind) && (
            <Row label="Width" hint="Match the real appliance width">
              <div className="grid grid-cols-4 gap-1.5">
                {(
                  {
                    fridge: [30, 33, 36, 48],
                    range: [24, 30, 36, 48],
                    dishwasher: [18, 24, 30],
                    hood: [30, 36, 48],
                  }[edgeKind] || []
                ).map((w) => (
                  <button
                    key={w}
                    type="button"
                    data-testid={`designer-edges-appwidth-${w}`}
                    onClick={() => onEdge?.({ appliance_width: w })}
                    className={`h-9 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      (Number(edge?.appliance_width) || 0) === w
                        ? "border-indigo-400 bg-indigo-500/15 text-indigo-200"
                        : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                    }`}
                  >
                    {w}"
                  </button>
                ))}
              </div>
            </Row>
          )}
          {edgeKind === "fridge" && (
            <Row label="Height" hint="Counter-depth or a tall panel-ready column">
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  ["standard", "Standard"],
                  ["counter", "Counter"],
                  ["tall", "Tall"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`designer-edges-appheight-${val}`}
                    onClick={() => onEdge?.({ appliance_height: val })}
                    className={`h-9 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                      (edge?.appliance_height || "standard") === val
                        ? "border-indigo-400 bg-indigo-500/15 text-indigo-200"
                        : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Row>
          )}
          {(edgeKind === "waterfall" || edgeKind === "mitre") && (
            <Row
              label={`${edgeKind === "waterfall" ? "Waterfall" : "Mitre"} depth (in)`}
              hint={`${dropSqft} ft² panel on this side`}
            >
              <Input
                data-testid="designer-edges-drop"
                type="number"
                step="any"
                value={edge?.drop_in ?? 0}
                onChange={(e) =>
                  onEdge?.({ drop_in: Number(e.target.value) || 0 })
                }
                className="h-10 border-zinc-800 bg-zinc-950"
              />
            </Row>
          )}
          {(edgeKind === "waterfall" || edgeKind === "mitre") &&
            Number(edge?.drop_in) > 0 && (
              <HidePanelRow edge={edge} onEdge={onEdge} />
            )}
        </div>
      )}

      <div className="space-y-2 border-t border-zinc-800 p-3">
        <Row label="Linked service">
          <Select
            value={style?.service_id || "none"}
            onValueChange={linkService}
            disabled={!can("admin:settings") || !style}
          >
            <SelectTrigger
              data-testid="designer-edge-service"
              className="h-10 border-zinc-800 bg-zinc-950 text-xs"
            >
              <SelectValue placeholder="Not linked" />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900">
              <SelectItem value="none">Not linked</SelectItem>
              {services.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>
        <div className="flex gap-2">
          <Button
            data-testid="designer-edges-copy-all"
            variant="outline"
            onClick={() => onCopyToAll?.(current)}
            className="h-10 flex-1 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
          >
            <Copy size={13} weight="bold" className="mr-1.5" /> All sides
          </Button>
          <Button
            data-testid="designer-edges-reset"
            variant="outline"
            onClick={() => onReset?.()}
            className="h-10 flex-1 border-red-500/30 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10"
          >
            <ArrowCounterClockwise size={13} weight="bold" className="mr-1.5" />{" "}
            Default
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EdgePanel;
